import { Capacitor } from '@capacitor/core'

import { RuStorePaymentChannel } from './rustoreChannel'
import { TochkaPaymentChannel } from './tochkaChannel'
import type { PaymentChannel, PaymentResumeRelease, PlanId, PurchaseOutcome } from './types'

interface Runtime { native: boolean; platform: string }
interface Channels { rustore: PaymentChannel; tochka: PaymentChannel }

export function selectPaymentChannel(runtime: Runtime, channels: Channels): PaymentChannel {
  return runtime.native ? channels.rustore : channels.tochka
}

let singleton: PaymentChannel | undefined
interface ResumeLifecycle {
  refs: number
  acquisition: Promise<PaymentResumeRelease>
  underlyingRelease?: PaymentResumeRelease
}
const resumeLifecycles = new WeakMap<PaymentChannel, ResumeLifecycle>()

export function getPaymentChannel(): PaymentChannel {
  if (!singleton) {
    singleton = selectPaymentChannel(
      { native: Capacitor.isNativePlatform(), platform: Capacitor.getPlatform() },
      { rustore: new RuStorePaymentChannel(), tochka: new TochkaPaymentChannel() },
    )
  }
  return singleton
}

export function acquirePaymentAppResume(channel: PaymentChannel = getPaymentChannel()): Promise<PaymentResumeRelease> {
  if (channel.provider !== 'rustore' || !channel.acquireAppResume) return Promise.resolve(async () => {})
  let lifecycle = resumeLifecycles.get(channel)
  if (!lifecycle) {
    let acquisition: Promise<PaymentResumeRelease>
    try { acquisition = Promise.resolve(channel.acquireAppResume()) } catch (error) { acquisition = Promise.reject(error) }
    const created: ResumeLifecycle = { refs: 0, acquisition }
    lifecycle = created
    resumeLifecycles.set(channel, created)
    void acquisition.then(
      (release) => { created.underlyingRelease = release },
      () => { if (resumeLifecycles.get(channel) === created) resumeLifecycles.delete(channel) },
    )
  }
  lifecycle.refs += 1
  const claimedLifecycle = lifecycle
  return lifecycle.acquisition.then(() => {
    let released = false
    return async () => {
      if (released) return
      released = true
      claimedLifecycle.refs = Math.max(0, claimedLifecycle.refs - 1)
      if (claimedLifecycle.refs !== 0 || resumeLifecycles.get(channel) !== claimedLifecycle) return
      resumeLifecycles.delete(channel)
      await claimedLifecycle.underlyingRelease?.()
    }
  })
}

export type CheckoutClaim =
  | { kind: 'acceptedNew' | 'joined'; promise: Promise<PurchaseOutcome> }
  | { kind: 'busy' }

export function createCheckoutCoordinator() {
  const flights = new WeakMap<PaymentChannel, { planId: PlanId; promise: Promise<PurchaseOutcome> }>()
  return {
    claim(channel: PaymentChannel, planId: PlanId): CheckoutClaim {
      const active = flights.get(channel)
      if (active) return active.planId === planId
        ? { kind: 'joined', promise: active.promise }
        : { kind: 'busy' }

      let resolve!: (outcome: PurchaseOutcome) => void
      let reject!: (error: unknown) => void
      const promise = new Promise<PurchaseOutcome>((onResolve, onReject) => { resolve = onResolve; reject = onReject })
      const flight = { planId, promise }
      flights.set(channel, flight)
      try { Promise.resolve(channel.purchase(planId)).then(resolve, reject) } catch (error) { reject(error) }
      const clear = () => { if (flights.get(channel) === flight) flights.delete(channel) }
      void promise.then(clear, clear)
      return { kind: 'acceptedNew', promise }
    },
  }
}

export const checkoutCoordinator = createCheckoutCoordinator()

export function paymentResultMessage(value: unknown): string {
  const status = (value as PurchaseOutcome)?.status
  if (status === 'cancelled' || (value as { code?: string })?.code === 'operation_cancelled') return ''
  if (status === 'pending') return 'Платёж проверяется. Доступ появится автоматически'
  if (status === 'acknowledgement_retry') return 'Платёж принят. Завершение покупки будет повторено автоматически'
  if (status === 'completed') return 'Покупка подтверждена'
  const code = (value as { code?: string })?.code
  if (code === 'rustore_unavailable' || code === 'rustore_outdated') return 'RuStore недоступен или требует обновления'
  if (code === 'rustore_unauthorized') return 'Войдите в RuStore и попробуйте снова'
  if (code === 'rustore_network_error') return 'Нет соединения. Проверьте интернет и повторите'
  if (code === 'rustore_product_unavailable') return 'Этот тариф временно недоступен в RuStore'
  if (code === 'purchase_pending_support') return 'Платёж принят и передан на ручную проверку. Обратитесь в поддержку'
  if (code === 'purchase_rejected') return 'Платёж не подтверждён. Попробуйте снова'
  if (code === 'purchase_in_progress') return 'Другой платёж уже выполняется'
  return 'Не удалось завершить покупку. Обратитесь в поддержку'
}

export type { PaymentChannel } from './types'
